import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { CssBaseline, Typography } from '@mui/material';
import Header from './Header';
import StyledComponents from './styles/StyledComponents';
import { motion, AnimatePresence } from 'framer-motion';
import { animations } from './styles/theme';
import LoadingFallback from './components/LoadingFallback';
import PerformanceWrapper from './components/PerformanceWrapper';
import { PriceProvider } from './pages/PriceContextOptimized';
import { SelectionProvider } from './customer/SelectionContextOptimized';
import { CustomerProvider } from './pages/CustomerContext';
import { VendorProvider } from './vendor/VendorContext';
import CustomerLayout from './customer/CustomerLayout';
import ActingAsIndicator from './components/ActingAsIndicator';
import api, { clearSessionPreservingProjects } from './api';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/700.css';
import { textureAnimations } from './styles/luxuryTextures';

// Lazy load all route components
// const CustomerLanding = lazy(() => import('./pages/CustomerLanding'));
const StaffPortalLanding = lazy(() => import('./pages/StaffPortalLanding'));
// const CustomerLogin = lazy(() => import('./components/CustomerLogin'));
// const BusinessLogin = lazy(() => import('./components/BusinessLogin'));
const Login = lazy(() => import('./pages/Login'));
const VendorRegistration = lazy(() => import('./pages/VendorRegistration'));
const Register = lazy(() => import('./pages/Register'));
const MortgageDetails = lazy(() => import('./customer/MortgageDetails'));
const HouseSelection = lazy(() => import('./pages/HouseSelection'));
const SelectionHub = lazy(() => import('./pages/SelectionHub'));
const SelectionSummary = lazy(() => import('./pages/SelectionSummary'));
const SalesDashboard = lazy(() => import('./pages/SalesDashboard'));
const PricingManagementPage = lazy(() => import('./pages/PricingManagementPage'));
const SalesReps = lazy(() => import('./pages/SalesReps'));
const Customers = lazy(() => import('./pages/Customers'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const IronBluffMapper = lazy(() => import('./pages/admin/IronBluffMapper'));
const ProjectManagerDashboard = lazy(() => import('./pages/ProjectManagerDashboard'));
const ProjectOverviewPage = lazy(() => import('./pages/ProjectOverviewPage'));
const SuperintendentDashboard = lazy(() => import('./pages/SuperintendentDashboard'));
const OperationsDashboard = lazy(() => import('./pages/OperationsDashboard'));
const OperationsDocumentsPage = lazy(() => import('./pages/OperationsDocumentsPage'));
const OptionPoResMappingPage = lazy(() => import('./pages/OptionPoResMappingPage'));
const OptionPoResAdvancedRulesPage = lazy(() => import('./pages/OptionPoResAdvancedRulesPage'));
const AccountingDashboard = lazy(() => import('./pages/AccountingDashboard'));
const CustomerSalesWizard = lazy(() => import('./pages/CustomerSalesWizard'));
const StaffVendorPortalView = lazy(() => import('./components/operations/StaffVendorPortalView'));
const TradeBidVisibilityPage = lazy(() => import('./pages/TradeBidVisibilityPage'));
// New Simple Vendor Components
const VendorDashboard = lazy(() => import('./vendor/pages/VendorDashboard'));
const VendorBidGridEnhanced = lazy(() => import('./vendor/pages/VendorBidGridEnhanced'));
const VendorBidComparison = lazy(() => import('./vendor/pages/VendorBidComparison'));
const VendorBidPriceEntry = lazy(() => import('./vendor/pages/VendorBidPriceEntry'));

const { AnimatedContainer } = StyledComponents;

const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [role, setRole] = useState(localStorage.getItem('role') || null);
  const [userAccess, setUserAccess] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user_access') || '{}'); } catch { return {}; }
  });
  const [, setActingAsCustomer] = useState(null);
  const hasCheckedAuth = useRef(false);
  

  useEffect(() => {
    if (hasCheckedAuth.current) return;
    hasCheckedAuth.current = true;

    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      const storedRole = localStorage.getItem('role');
      
      if (!token) {
        setRole(null);
        localStorage.removeItem('role');
        return;
      }

      // If we have both token and role, try to use them first
      if (storedRole) {
        setRole(storedRole);
      }

      try {
        const res = await api.get('/auth/profile');
        if (res.status === 200) {
          // Only update if role actually changed
          if (storedRole !== res.data.role) {
            localStorage.setItem('role', res.data.role);
            setRole(res.data.role);
          }
          if (res.data?.access && typeof res.data.access === 'object') {
            localStorage.setItem('user_access', JSON.stringify(res.data.access));
            setUserAccess(res.data.access);
          } else {
            localStorage.removeItem('user_access');
            setUserAccess({});
          }
          
          // For customers, ensure we have their customer ID
          if (res.data.role === 'Customer' && res.data.customerId) {
            console.log('Customer authenticated with ID:', res.data.customerId);
            localStorage.setItem('customerId', res.data.customerId);
          }
        }
      } catch (err) {
        if (err.response?.status === 401) {
          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              const refreshResponse = await api.post('/auth/refresh', { refresh_token: refreshToken });
              localStorage.setItem('access_token', refreshResponse.data.access_token);
              if (refreshResponse.data.refresh_token) {
                localStorage.setItem('refresh_token', refreshResponse.data.refresh_token);
              }
              const retryRes = await api.get('/auth/profile');
              if (retryRes.status === 200) {
                localStorage.setItem('role', retryRes.data.role);
                setRole(retryRes.data.role);
                if (retryRes.data?.access && typeof retryRes.data.access === 'object') {
                  localStorage.setItem('user_access', JSON.stringify(retryRes.data.access));
                  setUserAccess(retryRes.data.access);
                }
              }
            } else {
              console.log('🚨 App.js: No refresh token - checking if acting as customer');
              console.trace('No refresh token clear stack trace');
              
              // Check if sales rep is acting as customer
              const currentRole = localStorage.getItem('role');
              const isActingAs = localStorage.getItem('actingAsCustomer');
              
              if (currentRole === 'Sales' && isActingAs) {
                console.log('Sales rep acting as customer - maintaining session');
                // Don't clear everything, just remove customer-specific data
                localStorage.removeItem('customerId');
              } else {
                clearSessionPreservingProjects();
                setRole(null);
              }
            }
          } catch (refreshErr) {
            console.error('Refresh failed:', refreshErr);
            console.log('🚨 App.js: Refresh failed - checking if acting as customer');
            console.trace('Refresh failed clear stack trace');
            
            // Check if sales rep is acting as customer
            const currentRole = localStorage.getItem('role');
            const isActingAs = localStorage.getItem('actingAsCustomer');
            
            if (currentRole === 'Sales' && isActingAs) {
              console.log('Sales rep acting as customer - maintaining session during refresh failure');
            } else {
              clearSessionPreservingProjects();
              setRole(null);
            }
          }
        } else {
          console.error('Auth check failed:', err);
          console.log('🚨 App.js: Auth check failed - checking if acting as customer');
          console.trace('Auth check failed clear stack trace');
          
          // Check if sales rep is acting as customer
          const currentRole = localStorage.getItem('role');
          const isActingAs = localStorage.getItem('actingAsCustomer');
          
          if (currentRole === 'Sales' && isActingAs) {
            console.log('Sales rep acting as customer - maintaining session during auth check failure');
          } else {
            clearSessionPreservingProjects();
            setRole(null);
          }
        }
      }
    };

    checkAuth();
    
    // Restore acting as customer state
    const savedActingAs = localStorage.getItem('actingAsCustomer');
    const currentRole = localStorage.getItem('role');
    if (savedActingAs && currentRole === 'Sales') {
      try {
        setActingAsCustomer(JSON.parse(savedActingAs));
        console.log('Restored acting as customer state:', JSON.parse(savedActingAs));
      } catch (e) {
        console.error('Failed to parse actingAsCustomer:', e);
      }
    }
  }, []); // Remove navigate dependency to prevent loops

  // Navigation effect - only run when role changes and we're on login page
  useEffect(() => {
    if (role && (location.pathname === '/login' || location.pathname === '/business/login' || location.pathname === '/staff/login' || location.pathname === '/vendors/login')) {
      // Use setTimeout to prevent immediate re-render
      setTimeout(() => {
        switch (role) {
          case 'SuperAdmin': 
            navigate('/staff/admin-dashboard', { replace: true }); 
            break;
          case 'Admin': 
            navigate('/staff/admin-dashboard', { replace: true }); 
            break;
          case 'Sales': 
            navigate('/staff/sales-dashboard', { replace: true }); 
            break;
          case 'Operations':
            navigate('/staff/operations-dashboard', { replace: true });
            break;
          case 'ProjectManager':
            navigate('/staff/project-manager-dashboard', { replace: true });
            break;
          case 'Superintendent':
            navigate('/staff/superintendent-dashboard', { replace: true });
            break;
          case 'Vendor':
            navigate('/vendors/dashboard', { replace: true });
            break;
          case 'Customer': 
            navigate('/customer/selection', { replace: true }); 
            break;
          default: 
            navigate('/', { replace: true });
        }
      }, 100);
    }
  }, [role, location.pathname, navigate]);

  const ProtectedRoute = ({ element, allowedRoles }) => {
    // Don't cause re-renders by calling localStorage in render
    const storedRole = role;
    const customerId = localStorage.getItem('customerId');
    const currentPath = location.pathname;

    // If no role, redirect to appropriate login page
    if (!storedRole) {
      if (currentPath.startsWith('/staff') || currentPath.startsWith('/vendors')) {
        return <Navigate to="/business/login" replace />;
      }
      return <Navigate to="/login" replace />;
    }

    // Check if role is allowed
    if (!allowedRoles.includes(storedRole)) {
      if (currentPath.startsWith('/staff') || currentPath.startsWith('/vendors')) {
        return <Navigate to="/business/login" replace />;
      }
      return <Navigate to="/login" replace />;
    }

    const isSelectionPage =
      currentPath.startsWith('/customer/selection') ||
      currentPath.startsWith('/customer/location') ||
      currentPath === '/customer';

    // Sales role needs customer ID for selection pages
    if (storedRole === 'Sales' && isSelectionPage && !customerId) {
      console.log('⚠️ Sales user without customer ID on selection page - redirecting to sales dashboard');
      return <Navigate to="/staff/sales-dashboard" replace />;
    }
    
    // Debug customer role on selection pages
    if (storedRole === 'Customer' && isSelectionPage) {
      console.log('🚶 Customer on selection page:', { customerId, currentPath });
    }

    const accessByPath = {
      '/staff/sales-dashboard': 'salesDashboard',
      '/staff/pricing-management': 'pricingManagement',
      '/staff/sales-reps': 'salesReps',
      '/staff/customers': 'customers',
      '/staff/admin-users': 'adminUsers',
      '/staff/admin-dashboard': 'adminDashboard',
      '/staff/operations-dashboard': 'operationsDashboard',
      '/staff/operations-documents': 'operationsDashboard',
      '/staff/trade-bid-visibility': 'operationsDashboard',
      '/staff/project-manager-dashboard': 'projectManagerDashboard',
      '/staff/superintendent-dashboard': 'superintendentDashboard',
      '/staff/accounting-dashboard': 'accountingDashboard',
      '/vendors/dashboard': 'vendorPortal',
      '/customer/selection': 'customerPortal',
      '/customer/selections': 'customerPortal',
      '/customer/mortgage': 'customerPortal',
      '/customer/selection-summary': 'customerPortal',
    };
    const matchedAccessKey = Object.entries(accessByPath).find(([prefix]) => currentPath.startsWith(prefix))?.[1];
    if (matchedAccessKey && storedRole !== 'SuperAdmin') {
      // Merge localStorage so first paint after login sees access set in Login.js before profile checkAuth runs.
      let accessFromStorage = {};
      try {
        accessFromStorage = JSON.parse(localStorage.getItem('user_access') || '{}') || {};
      } catch {
        accessFromStorage = {};
      }
      const effectiveAccess = { ...userAccess, ...accessFromStorage };
      const hasAccess = !!effectiveAccess[matchedAccessKey];
      if (!hasAccess) {
        return <Navigate to="/business/login" replace />;
      }
    }

    return element;
  };

  const CustomerRoutes = () => (
    <CustomerLayout>
      <SelectionProvider>
        <Suspense fallback={<LoadingFallback />}>
          <Outlet />
        </Suspense>
      </SelectionProvider>
    </CustomerLayout>
  );

  // Determine if we should show the header
  const showHeader = !['/', '/staff'].includes(location.pathname) && 
                    !location.pathname.endsWith('/landing');

  const contentMaxWidth = ['/', '/staff'].includes(location.pathname)
    ? '100%'
    : (location.pathname.startsWith('/staff/operations-documents') || location.pathname.startsWith('/staff/trade-bid-visibility'))
      ? 'min(1720px, 100%)'
      : '1200px';

  return (
    <>
      <CssBaseline />
      {showHeader && <Header role={role} />}
      {role === 'Sales' && <ActingAsIndicator />}
      <AnimatePresence mode="wait">
        {role === null && !['/', '/staff'].includes(location.pathname) && !location.pathname.includes('/login') && (
          <motion.div
            {...animations.fadeIn}
            exit={{ opacity: 0 }}
            style={{ maxWidth: '800px', margin: '2rem auto', padding: '0 1rem' }}
          >
            <Typography variant="h4">Loading...</Typography>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatedContainer
        {...animations.fadeIn}
        style={{ 
          maxWidth: contentMaxWidth, 
          margin: '0 auto', 
          padding: showHeader ? '5rem 1rem 2rem' : '0' 
        }}
      >
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
          {/* Public Landing Pages */}
          <Route path="/" element={<Login setRole={setRole} />} />
          <Route path="/staff" element={<StaffPortalLanding />} />
          
          {/* Auth Routes */}
          {/* <Route path="/login" element={<CustomerLogin setRole={setRole} />} /> */}
          {/* <Route path="/business/login" element={<BusinessLogin setRole={setRole} />} /> */}
          <Route path="/login" element={<Login setRole={setRole} />} />
          <Route path="/business/login" element={<Login setRole={setRole} />} />
          <Route path="/vendors/register" element={<VendorRegistration />} />
          <Route path="/register" element={<Register />} />
          
          {/* Protected Customer Routes */}
          <Route path="/customer" element={<ProtectedRoute element={<CustomerRoutes />} allowedRoles={['Customer', 'Sales', 'Admin', 'SuperAdmin']} />}>
            <Route index element={<Navigate to="/customer/selection" replace />} />
            <Route path="selection" element={<PerformanceWrapper id="HouseSelection"><HouseSelection /></PerformanceWrapper>} />
            <Route path="selections" element={<PerformanceWrapper id="SelectionHub"><SelectionHub /></PerformanceWrapper>} />
            <Route path="mortgage" element={<MortgageDetails />} />
            {/* Legacy location routes — redirect to the unified SelectionHub */}
            <Route path="location/*" element={<Navigate to="/customer/selections" replace />} />
            <Route path="selection-summary" element={<PerformanceWrapper id="SelectionSummary"><SelectionSummary /></PerformanceWrapper>} />
          </Route>

          {/* Staff Portal Routes */}
          <Route path="/staff/sales-dashboard" element={<ProtectedRoute element={<SalesDashboard />} allowedRoles={['Sales', 'Admin', 'SuperAdmin']} />} />
          <Route
            path="/staff/sales-wizard/:customerId"
            element={<ProtectedRoute element={<CustomerSalesWizard />} allowedRoles={['Sales', 'Admin', 'SuperAdmin']} />}
          />
          <Route path="/staff/pricing-management" element={<ProtectedRoute element={<PricingManagementPage />} allowedRoles={['Sales', 'Admin', 'SuperAdmin']} />} />
          <Route path="/staff/sales-reps" element={<ProtectedRoute element={<SalesReps />} allowedRoles={['Sales', 'Admin', 'SuperAdmin']} />} />
          <Route path="/staff/customers" element={<ProtectedRoute element={<Customers />} allowedRoles={['Sales', 'Admin', 'SuperAdmin']} />} />
          <Route path="/staff/admin-users" element={<ProtectedRoute element={<AdminUsers />} allowedRoles={['SuperAdmin']} />} />
          <Route path="/staff/admin-dashboard" element={<ProtectedRoute element={<AdminDashboard />} allowedRoles={['Admin', 'SuperAdmin']} />} />
          <Route path="/staff/admin/map-iron-bluff" element={<ProtectedRoute element={<IronBluffMapper />} allowedRoles={['Admin', 'SuperAdmin']} />} />
          <Route path="/staff/operations-dashboard" element={<ProtectedRoute element={<OperationsDashboard />} allowedRoles={['Operations', 'FieldManager', 'Admin', 'SuperAdmin']} />} />
          <Route
            path="/staff/operations-documents"
            element={
              <ProtectedRoute element={<OperationsDocumentsPage />} allowedRoles={['Operations', 'FieldManager', 'Admin', 'SuperAdmin']} />
            }
          />
          <Route
            path="/staff/option-po-res-mapping"
            element={<ProtectedRoute element={<OptionPoResMappingPage />} allowedRoles={['Operations', 'FieldManager', 'Admin', 'SuperAdmin']} />}
          />
          <Route
            path="/staff/option-po-res-advanced"
            element={<ProtectedRoute element={<OptionPoResAdvancedRulesPage />} allowedRoles={['Operations', 'FieldManager', 'Admin', 'SuperAdmin']} />}
          />
          <Route path="/staff/operations-dashboard/vendor-view/:vendorId" element={<ProtectedRoute element={<StaffVendorPortalView />} allowedRoles={['Operations', 'FieldManager', 'Admin', 'SuperAdmin']} />} />
          <Route path="/staff/trade-bid-visibility" element={<ProtectedRoute element={<TradeBidVisibilityPage />} allowedRoles={['Operations', 'FieldManager', 'Admin', 'SuperAdmin']} />} />
          <Route path="/staff/project-manager-dashboard" element={<ProtectedRoute element={<ProjectManagerDashboard />} allowedRoles={['ProjectManager', 'Admin', 'SuperAdmin']} />} />
          <Route path="/staff/project-overview" element={<ProtectedRoute element={<ProjectOverviewPage />} allowedRoles={['ProjectManager', 'Admin', 'SuperAdmin']} />} />
          <Route path="/staff/superintendent-dashboard" element={<ProtectedRoute element={<SuperintendentDashboard />} allowedRoles={['Superintendent', 'FieldManager', 'Admin', 'SuperAdmin']} />} />
          <Route path="/staff/accounting-dashboard" element={<ProtectedRoute element={<AccountingDashboard />} allowedRoles={['Admin', 'SuperAdmin', 'Operations']} />} />
          
          {/* Vendor Portal Routes */}
          <Route path="/vendors/dashboard" element={
            <ProtectedRoute 
              element={
                <VendorProvider>
                  <VendorDashboard />
                </VendorProvider>
              } 
              allowedRoles={['Vendor', 'FieldManager', 'Admin', 'SuperAdmin']} 
            />
          } />
          {/* Redirect old grid route to enhanced grid */}
          <Route path="/vendors/bids/grid" element={<Navigate to="/vendors/bids/grid-enhanced" replace />} />
          
          {/* Legacy grid still accessible at explicit path */}
          <Route path="/vendors/bids/grid-legacy" element={
            <ProtectedRoute 
              element={
                <VendorProvider>
                  <VendorBidGridEnhanced />
                </VendorProvider>
              } 
              allowedRoles={['Vendor', 'FieldManager', 'Admin', 'SuperAdmin']} 
            />
          } />
          <Route path="/vendors/bids/grid-enhanced" element={
            <ProtectedRoute 
              element={
                <VendorProvider>
                  <VendorBidGridEnhanced />
                </VendorProvider>
              } 
              allowedRoles={['Vendor', 'FieldManager', 'Admin', 'SuperAdmin']} 
            />
          } />
          <Route path="/vendors/bids/comparison" element={
            <ProtectedRoute 
              element={
                <VendorProvider>
                  <VendorBidComparison />
                </VendorProvider>
              } 
              allowedRoles={['Vendor', 'FieldManager', 'Admin', 'SuperAdmin']} 
            />
          } />
          <Route path="/vendors/bids/price-entry" element={
            <ProtectedRoute
              element={
                <VendorProvider>
                  <VendorBidPriceEntry />
                </VendorProvider>
              }
              allowedRoles={['Vendor', 'FieldManager', 'Admin', 'SuperAdmin']}
            />
          } />
          <Route path="/vendors/bids/submit" element={<Navigate to="/vendors/bids/grid-enhanced" replace />} />
          
          {/* Legacy Routes - Redirect to new structure */}
          <Route path="/sales-dashboard" element={<Navigate to="/staff/sales-dashboard" replace />} />
          {/* Old location paths — redirect to unified SelectionHub */}
          <Route path="/location/*" element={<Navigate to="/customer/selections" replace />} />
          <Route path="/admin-dashboard" element={<Navigate to="/staff/admin-dashboard" replace />} />
          <Route path="/admin-users" element={<Navigate to="/staff/admin-users" replace />} />
          <Route path="/operations-dashboard" element={<Navigate to="/staff/operations-dashboard" replace />} />
          <Route path="/project-manager-dashboard" element={<Navigate to="/staff/project-manager-dashboard" replace />} />
          <Route path="/superintendent-dashboard" element={<Navigate to="/staff/superintendent-dashboard" replace />} />
          <Route path="/vendor-dashboard" element={<Navigate to="/vendors/dashboard" replace />} />
          <Route path="/staff/login" element={<Navigate to="/business/login" replace />} />
          <Route path="/vendors/login" element={<Login setRole={setRole} />} />
          <Route path="/vendors" element={<Navigate to="/vendors/login" replace />} />
          
          {/* Catch all possible vendor bid routes and redirect to integration */}
          <Route path="/vendor/bids" element={<Navigate to="/vendors/bids/grid-enhanced" replace />} />
          <Route path="/vendor/bids/*" element={<Navigate to="/vendors/bids/grid-enhanced" replace />} />
          <Route path="/vendor/bid" element={<Navigate to="/vendors/bids/grid-enhanced" replace />} />
          <Route path="/vendor/bid/*" element={<Navigate to="/vendors/bids/grid-enhanced" replace />} />
          <Route path="/vendor/*" element={<Navigate to="/vendors/*" replace />} />
          <Route path="/house-selection" element={<Navigate to="/customer/selection" replace />} />
          <Route path="/mortgage-details" element={<Navigate to="/customer/mortgage" replace />} />
          <Route path="/location/*" element={<Navigate to={`/customer/location/${location.pathname.slice(10)}`} replace />} />
          <Route path="/selection-summary" element={<Navigate to="/customer/selection-summary" replace />} />
          <Route path="/selection" element={<Navigate to="/customer/selection" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AnimatedContainer>
    </>
  );
};

const App = () => (
  <CustomerProvider>
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <PriceProvider>
        <style>{textureAnimations}</style>
        <AppContent />
      </PriceProvider>
    </BrowserRouter>
  </CustomerProvider>
);

export default App;